import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  FileSpreadsheet, Plus, History, FileText, Link as LinkIcon,
  AlertCircle, CheckCircle, Mail, Calendar, Copy, Trash2,
  Download, BookOpen, Clock, Play, Pause, Search
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis, 
  Radar, 
  ResponsiveContainer,
  Legend
} from 'recharts';

// Normalizes text for search: lowercase and strips accents/special characters
const normalizeSearchText = (text) => {
  return (text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim();
};

const ExcelAssessmentPage = () => {
  const { user } = useAuth();
  const ALL_TABS = ['new', 'description', 'history', 'reports', 'public-links'];
  const allowedTabs = ALL_TABS;
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  const [activeTab, setActiveTab] = useState(allowedTabs.includes('new') ? 'new' : (allowedTabs[0] || 'new'));
  const [loading, setLoading] = useState(false);
  
  // Questions and assessment state
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [assessmentStarted, setAssessmentStarted] = useState(false);
  
  // Timer state
  const [timerEnabled, setTimerEnabled] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(30 * 60); // 30 minutes in seconds
  const [timerRunning, setTimerRunning] = useState(false);
  const [startTime, setStartTime] = useState(null);
  
  // Form state
  const [participantName, setParticipantName] = useState('');
  const [participantEmail, setParticipantEmail] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  
  // Employees
  const [employees, setEmployees] = useState([]);
  
  // Assessment results
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);

  // History filters
  const [historyNameFilter, setHistoryNameFilter] = useState('');
  const [historyDateFilter, setHistoryDateFilter] = useState('');
  const filteredHistoryAssessments = assessments.filter(assessment => {
    const nameMatch = !historyNameFilter || normalizeSearchText(assessment.participant_name).includes(normalizeSearchText(historyNameFilter));
    const dateMatch = !historyDateFilter || new Date(assessment.assessment_date).toISOString().slice(0, 10) === historyDateFilter;
    return nameMatch && dateMatch;
  });

  // Public links
  const [publicLinks, setPublicLinks] = useState([]);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkParticipantName, setLinkParticipantName] = useState('');
  const [linkParticipantEmail, setLinkParticipantEmail] = useState('');

  const questionsPerPage = 10;
  const totalPages = Math.ceil(30 / questionsPerPage);
  const currentPage = Math.floor(currentQuestionIndex / questionsPerPage);

  useEffect(() => {
    fetchEmployees();
    fetchAssessments();
    fetchPublicLinks();
  }, []);

  useEffect(() => {
    let interval;
    if (timerRunning && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setTimerRunning(false);
            handleSubmitAssessment();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerRunning, timeRemaining]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/employees?active_only=true`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchAssessments = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/excel/assessments`);
      setAssessments(response.data);
    } catch (error) {
      console.error('Error fetching assessments:', error);
    }
  };

  const fetchPublicLinks = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/excel/public-links`);
      setPublicLinks(response.data || []);
    } catch (error) {
      console.error('Error fetching public links:', error);
      setPublicLinks([]);
    }
  };

  const handleExcluirAssessment = async (id, nome) => {
    if (!window.confirm(`Tem certeza que deseja excluir a avaliação de "${nome}"?`)) return;
    try {
      await axios.delete(`${backendUrl}/api/excel/assessment/${id}`);
      toast({ title: "Avaliação Excluída", description: "A avaliação foi removida com sucesso" });
      fetchAssessments();
    } catch (error) {
      console.error('Error deleting assessment:', error);
      toast({ title: "Erro", description: "Erro ao excluir avaliação", variant: "destructive" });
    }
  };

  const handleExcluirLink = async (id, nome) => {
    if (!window.confirm(`Tem certeza que deseja excluir o link de "${nome}"?`)) return;
    try {
      await axios.delete(`${backendUrl}/api/excel/public-link/${id}`);
      toast({ title: "Link Excluído", description: "O link foi removido com sucesso" });
      fetchPublicLinks();
    } catch (error) {
      console.error('Error deleting link:', error);
      toast({ title: "Erro", description: "Erro ao excluir link", variant: "destructive" });
    }
  };

  const startNewAssessment = async () => {
    if (!participantName.trim()) {
      toast({
        title: "Campo obrigatório",
        description: "Por favor, informe o nome do participante",
        variant: "destructive"
      });
      return;
    }

    if (participantEmail.trim()) {
      try {
        setLoading(true);
        const response = await axios.post(`${backendUrl}/api/excel/assessment/start`, {
          participant_name: participantName.trim(),
          participant_email: participantEmail.trim()
        });
        if (response.data.email_sent) {
          toast({
            title: "Link enviado!",
            description: `Link da avaliação enviado com sucesso para ${participantEmail}`,
          });
        } else {
          toast({
            title: "Link criado",
            description: `Link criado, mas não foi possível enviar o email para ${participantEmail}. Copie o link manualmente na aba Links Públicos.`,
            variant: "destructive"
          });
        }
        setAssessmentStarted(false);
      } catch (error) {
        console.error('Error creating assessment link:', error);
        toast({
          title: "Erro",
          description: "Não foi possível criar o link da avaliação",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/excel/questions`);
      setQuestions(response.data.questions);
      setAssessmentStarted(true);
      setCurrentQuestionIndex(0);
      setAnswers({});
      if (timerEnabled) {
        setStartTime(Date.now());
        setTimerRunning(true);
      }
      
      toast({
        title: "Avaliação iniciada",
        description: "Boa sorte!",
      });
    } catch (error) {
      console.error('Error starting assessment:', error);
      toast({
        title: "Erro",
        description: "Não foi possível iniciar a avaliação",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, option) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: option
    }));
  };

  const handleSubmitAssessment = async () => {
    const answeredCount = Object.keys(answers).length;
    if (answeredCount < 30) {
      const confirmSubmit = window.confirm(
        `Você respondeu ${answeredCount} de 30 questões. Deseja realmente enviar a avaliação?`
      );
      if (!confirmSubmit) return;
    }

    try {
      setLoading(true);
      setTimerRunning(false);
      
      const timeMinutes = startTime ? Math.round((Date.now() - startTime) / 1000 / 60) : null;
      
      const formattedAnswers = Object.entries(answers).map(([questionId, selectedOption]) => ({
        question_id: parseInt(questionId),
        selected_option: selectedOption
      }));

      const response = await axios.post(`${backendUrl}/api/excel/assessment`, {
        participant_name: participantName,
        participant_email: participantEmail || null,
        employee_id: selectedEmployeeId || null,
        answers: formattedAnswers,
        time_minutes: timeMinutes
      });

      toast({
        title: "Avaliação concluída!",
        description: `Nível: ${response.data.detected_level} (${response.data.detected_level}) - Score: ${response.data.score_geral}%`,
      });

      // Reset form
      setAssessmentStarted(false);
      setParticipantName('');
      setParticipantEmail('');
      setSelectedEmployeeId('');
      setAnswers({});
      setCurrentQuestionIndex(0);
      
      // Refresh assessments list
      fetchAssessments();
      
      // Switch to history tab
      setActiveTab('history');
    } catch (error) {
      console.error('Error submitting assessment:', error);
      toast({
        title: "Erro",
        description: "Não foi possível enviar a avaliação",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePublicLink = async () => {
    if (!linkParticipantName.trim() || !linkParticipantEmail.trim()) {
      toast({
        title: "Campos obrigatórios",
        description: "Por favor, informe nome e email",
        variant: "destructive"
      });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${backendUrl}/api/excel/public-link`, {
        participant_name: linkParticipantName,
        participant_email: linkParticipantEmail
      });

      const publicLink = `${window.location.origin}/excel/public/${response.data.token}`;
      
      // Copy to clipboard
      await navigator.clipboard.writeText(publicLink);
      
      toast({
        title: "Link criado!",
        description: "Link copiado para a área de transferência",
      });

      // Reset form
      setLinkParticipantName('');
      setLinkParticipantEmail('');
      setShowLinkForm(false);
      
      // Refresh links
      fetchPublicLinks();
    } catch (error) {
      console.error('Error creating public link:', error);
      toast({
        title: "Erro",
        description: "Não foi possível criar o link",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const copyLinkToClipboard = async (token) => {
    const publicLink = `${window.location.origin}/excel/public/${token}`;
    await navigator.clipboard.writeText(publicLink);
    toast({
      title: "Link copiado!",
      description: "Link copiado para a área de transferência",
    });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getRadarData = (assessment) => {
    return [
      { subject: 'Fundamentals', score: assessment.fundamentals_score, fullMark: 100 },
      { subject: 'Formulas', score: assessment.formulas_score, fullMark: 100 },
      { subject: 'Analysis', score: assessment.analysis_score, fullMark: 100 },
      { subject: 'Macros/VBA', score: assessment.macros_score, fullMark: 100 }
    ];
  };

  const getLevelColor = (level) => {
    const colors = {
      'Básico': '#ef4444',
      'Intermediário': '#eab308',
      'Avançado': '#22c55e'
    };
    return colors[level] || '#6b7280';
  };

  const handleDownloadPDF = async (assessmentId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${backendUrl}/api/excel/assessment/${assessmentId}/pdf`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `excel_assessment_${assessmentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({
        title: "PDF baixado!",
        description: "O relatório foi baixado com sucesso",
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({
        title: "Erro",
        description: "Não foi possível baixar o PDF",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <FileSpreadsheet className="h-8 w-8 text-blue-600" />
          <h1 className="text-3xl font-bold">Avaliação de Excel</h1>
        </div>
        <p className="text-gray-600">
          Avalie o nível de proficiência em inglês (Nível: A1-C2)
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          {allowedTabs.includes('new') && <TabsTrigger value="new">
            <Plus className="h-4 w-4 mr-2" />
            Nova Avaliação
          </TabsTrigger>}
          {allowedTabs.includes('description') && <TabsTrigger value="description">
            <BookOpen className="h-4 w-4 mr-2" />
            Descrição
          </TabsTrigger>}
          {allowedTabs.includes('history') && <TabsTrigger value="history">
            <History className="h-4 w-4 mr-2" />
            Histórico
          </TabsTrigger>}
          {allowedTabs.includes('reports') && <TabsTrigger value="reports">
            <FileText className="h-4 w-4 mr-2" />
            Relatórios
          </TabsTrigger>}
          {allowedTabs.includes('public-links') && <TabsTrigger value="public-links">
            <LinkIcon className="h-4 w-4 mr-2" />
            Links Públicos
          </TabsTrigger>}
        </TabsList>

        {/* Nova Avaliação Tab */}
        {allowedTabs.includes('new') && <TabsContent value="new" className="space-y-4">
          {!assessmentStarted ? (
            <Card>
              <CardHeader>
                <CardTitle>Iniciar Nova Avaliação de Excel</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Nome do Participante *
                  </label>
                  <input
                    type="text"
                    value={participantName}
                    onChange={(e) => setParticipantName(e.target.value.replace(/\d/g, ''))}
                    className="w-full p-2 border rounded"
                    placeholder="Digite o nome completo"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Email do Participante
                  </label>
                  <input
                    type="email"
                    value={participantEmail}
                    onChange={(e) => setParticipantEmail(e.target.value)}
                    className="w-full p-2 border rounded"
                    placeholder="email@exemplo.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Vincular a Colaborador (opcional)
                  </label>
                  <select
                    value={selectedEmployeeId}
                    onChange={(e) => setSelectedEmployeeId(e.target.value)}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">Selecione um colaborador</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {emp.nome}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={timerEnabled}
                    onChange={(e) => setTimerEnabled(e.target.checked)}
                    id="timer-check"
                  />
                  <label htmlFor="timer-check" className="text-sm">
                    Ativar timer (30 minutos)
                  </label>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-900">
                    <strong>Informações da Avaliação:</strong>
                  </p>
                  <ul className="text-sm text-blue-800 mt-2 space-y-1">
                    <li>• 30 questões (Fundamentals, Formulas, Analysis, Macros/VBA)</li>
                    <li>• Tempo sugerido: 30 minutos</li>
                    <li>• Classificação: Básico, Intermediário, Avançado</li>
                  </ul>
                </div>

                <Button 
                  onClick={startNewAssessment}
                  disabled={loading || !participantName.trim()}
                  className="w-full"
                >
                  {loading ? 'Carregando...' : 'Iniciar Avaliação'}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>
                    Questão {currentQuestionIndex + 1} de 30
                  </CardTitle>
                  {timerEnabled && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5" />
                      <span className={`font-mono text-lg ${timeRemaining < 300 ? 'text-red-600' : ''}`}>
                        {formatTime(timeRemaining)}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setTimerRunning(!timerRunning)}
                      >
                        {timerRunning ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                      </Button>
                    </div>
                  )}
                </div>
                <div className="mt-2 bg-gray-200 h-2 rounded-full">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${((currentQuestionIndex + 1) / 40) * 100}%` }}
                  />
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {questions[currentQuestionIndex] && (
                  <>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-lg font-medium">
                        {questions[currentQuestionIndex].question}
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        Categoria: {questions[currentQuestionIndex].category}
                      </p>
                    </div>

                    <div className="space-y-3">
                      {Object.entries(questions[currentQuestionIndex].options).map(([key, value]) => (
                        <button
                          key={key}
                          onClick={() => handleAnswer(questions[currentQuestionIndex].id, key)}
                          className={`w-full p-4 text-left border-2 rounded-lg transition-all ${
                            answers[questions[currentQuestionIndex].id] === key
                              ? 'border-blue-600 bg-blue-50'
                              : 'border-gray-200 hover:border-blue-300'
                          }`}
                        >
                          <span className="font-semibold mr-2">{key.toUpperCase()})</span>
                          {value}
                        </button>
                      ))}
                    </div>

                    <div className="flex justify-between items-center pt-4">
                      <Button
                        variant="outline"
                        onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
                        disabled={currentQuestionIndex === 0}
                      >
                        Anterior
                      </Button>

                      <span className="text-sm text-gray-600">
                        {Object.keys(answers).length} de 30 respondidas
                      </span>

                      {currentQuestionIndex < 29 ? (
                        <Button
                          onClick={() => setCurrentQuestionIndex(prev => Math.min(29, prev + 1))}
                        >
                          Próxima
                        </Button>
                      ) : (
                        <Button
                          onClick={handleSubmitAssessment}
                          disabled={loading}
                        >
                          {loading ? 'Enviando...' : 'Finalizar Avaliação'}
                        </Button>
                      )}
                    </div>

                    {/* Question Navigation */}
                    <div className="pt-4 border-t">
                      <p className="text-sm font-medium mb-2">Navegação Rápida:</p>
                      <div className="grid grid-cols-10 gap-2">
                        {questions.map((q, idx) => (
                          <button
                            key={idx}
                            onClick={() => setCurrentQuestionIndex(idx)}
                            className={`p-2 text-sm rounded ${
                              idx === currentQuestionIndex
                                ? 'bg-blue-600 text-white'
                                : answers[q.id]
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {idx + 1}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>}

        {/* Descrição Tab */}
        {allowedTabs.includes('description') && <TabsContent value="description">
          <Card>
            <CardHeader>
              <CardTitle>Sobre a Avaliação de Excel</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2">Metodologia</h3>
                <p className="text-gray-700">
                  Esta avaliação mede o nível de proficiência em Microsoft Excel através de questões práticas e teóricas 
                  que cobrem desde fundamentos básicos até recursos avançados como macros e VBA.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-2">Estrutura da Avaliação</h3>
                <ul className="space-y-2 text-gray-700">
                  <li><strong>Fundamentals (8 questões):</strong> Avalia conhecimentos básicos de Excel como atalhos, formatação e navegação.</li>
                  <li><strong>Formulas (8 questões):</strong> Mede domínio de fórmulas como VLOOKUP, IF, SUMIF e funções de texto.</li>
                  <li><strong>Analysis (7 questões):</strong> Testa habilidades em análise de dados, tabelas dinâmicas e gráficos.</li>
                  <li><strong>Macros/VBA (7 questões):</strong> Avalia conhecimento em automação, macros e programação VBA.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-2">Níveis de Proficiência</h3>
                <div className="space-y-3">
                  <div className="p-3 bg-red-50 rounded border-l-4 border-red-500">
                    <p className="font-semibold text-red-900">Básico (0-40%)</p>
                    <p className="text-sm text-red-800">Conhece operações básicas como digitação, formatação simples e fórmulas elementares (soma, subtração).</p>
                  </div>
                  <div className="p-3 bg-yellow-50 rounded border-l-4 border-yellow-500">
                    <p className="font-semibold text-yellow-900">Intermediário (40-70%)</p>
                    <p className="text-sm text-yellow-800">Domina fórmulas intermediárias (VLOOKUP, IF, SUMIF), tabelas dinâmicas, gráficos e análise de dados.</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded border-l-4 border-green-500">
                    <p className="font-semibold text-green-900">Avançado (70-100%)</p>
                    <p className="text-sm text-green-800">Proficiente em recursos avançados incluindo Power Query, análise What-If, macros e programação VBA.</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>}

        {/* Histórico Tab */}
        {allowedTabs.includes('history') && <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Avaliações</CardTitle>
            </CardHeader>
            <CardContent>
              {assessments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileSpreadsheet className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhuma avaliação realizada ainda</p>
                </div>
              ) : (
                <>
                  <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={historyNameFilter}
                        onChange={(e) => setHistoryNameFilter(e.target.value)}
                        placeholder="Buscar por nome do funcionário..."
                        className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <input
                      type="date"
                      value={historyDateFilter}
                      onChange={(e) => setHistoryDateFilter(e.target.value)}
                      className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {(historyNameFilter || historyDateFilter) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setHistoryNameFilter(''); setHistoryDateFilter(''); }}
                      >
                        Limpar Filtros
                      </Button>
                    )}
                  </div>
                  {filteredHistoryAssessments.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>Nenhuma avaliação encontrada para os filtros aplicados</p>
                    </div>
                  ) : (
                <div className="space-y-3">
                  {filteredHistoryAssessments.map(assessment => (
                    <div key={assessment.id} className="p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold">{assessment.participant_name}</h3>
                          <p className="text-sm text-gray-600">{assessment.participant_email}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {new Date(assessment.assessment_date).toLocaleDateString('pt-BR')}
                            </span>
                            {assessment.time_minutes && (
                              <span className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                {assessment.time_minutes} min
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div 
                            className="px-3 py-1 rounded-full text-sm font-semibold"
                            style={{ 
                              backgroundColor: `${getLevelColor(assessment.detected_level)}20`,
                              color: getLevelColor(assessment.detected_level)
                            }}
                          >
                            {assessment.detected_level} - {assessment.detected_level}
                          </div>
                          <p className="text-2xl font-bold mt-2">{assessment.score_geral}%</p>
                        </div>
                      </div>
                      <div className="mt-3 flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedAssessment(assessment);
                            setActiveTab('reports');
                          }}
                        >
                          <FileText className="h-4 w-4 mr-1" />
                          Ver Relatório
                        </Button>
                        {user?.is_admin && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleExcluirAssessment(assessment.id, assessment.participant_name)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>}

        {/* Relatórios Tab */}
        {allowedTabs.includes('reports') && <TabsContent value="reports">
          <Card>
            <CardHeader>
              <CardTitle>Relatórios Detalhados</CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedAssessment ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Selecione uma avaliação no histórico para ver o relatório</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Header */}
                  <div className="flex justify-between items-start pb-4 border-b">
                    <div>
                      <h2 className="text-2xl font-bold">{selectedAssessment.participant_name}</h2>
                      <p className="text-gray-600">{selectedAssessment.participant_email}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        Avaliado em {new Date(selectedAssessment.assessment_date).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <Button onClick={() => handleDownloadPDF(selectedAssessment.id)}>
                      <Download className="h-4 w-4 mr-2" />
                      Download PDF
                    </Button>
                  </div>

                  {/* Score Summary */}
                  <div className="grid grid-cols-2 gap-4">
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <p className="text-sm text-gray-600 mb-1">Nível Nível</p>
                          <div 
                            className="text-4xl font-bold"
                            style={{ color: getLevelColor(selectedAssessment.detected_level) }}
                          >
                            {selectedAssessment.detected_level}
                          </div>
                          <p className="text-lg mt-1">{selectedAssessment.detected_level}</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <p className="text-sm text-gray-600 mb-1">Score Geral</p>
                          <div className="text-4xl font-bold text-blue-600">
                            {selectedAssessment.score_geral}%
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            {selectedAssessment.acertos}/{selectedAssessment.total_questoes} acertos
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Radar Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Desempenho por Categoria</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <RadarChart data={getRadarData(selectedAssessment)}>
                          <PolarGrid />
                          <PolarAngleAxis dataKey="subject" />
                          <PolarRadiusAxis angle={90} domain={[0, 100]} />
                          <Radar
                            name="Score"
                            dataKey="score"
                            stroke="#3b82f6"
                            fill="#3b82f6"
                            fillOpacity={0.6}
                          />
                          <Legend />
                        </RadarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Category Breakdown */}
                  <div className="grid grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="pt-6">
                        <h3 className="font-semibold mb-2">Fundamentals</h3>
                        <p className="text-3xl font-bold text-blue-600">
                          {selectedAssessment.fundamentals_score}%
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <h3 className="font-semibold mb-2">Formulas</h3>
                        <p className="text-3xl font-bold text-green-600">
                          {selectedAssessment.formulas_score}%
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <h3 className="font-semibold mb-2">Analysis</h3>
                        <p className="text-3xl font-bold text-purple-600">
                          {selectedAssessment.analysis_score}%
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <h3 className="font-semibold mb-2">Macros/VBA</h3>
                        <p className="text-3xl font-bold text-orange-600">
                          {selectedAssessment.macros_score}%
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Statistics */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Estatísticas</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-4 gap-4 text-center">
                        <div>
                          <p className="text-sm text-gray-600">Total</p>
                          <p className="text-2xl font-bold">{selectedAssessment.total_questoes}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Acertos</p>
                          <p className="text-2xl font-bold text-green-600">{selectedAssessment.acertos}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Erros</p>
                          <p className="text-2xl font-bold text-red-600">{selectedAssessment.erros}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Não Respondidas</p>
                          <p className="text-2xl font-bold text-gray-600">{selectedAssessment.nao_respondidas}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>}

        {/* Public Links Tab */}
        {allowedTabs.includes('public-links') && <TabsContent value="public-links">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Links Públicos</CardTitle>
                <Button onClick={() => setShowLinkForm(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Novo Link
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {showLinkForm && (
                <Card className="bg-blue-50">
                  <CardContent className="pt-6 space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Nome *</label>
                      <input
                        type="text"
                        value={linkParticipantName}
                        onChange={(e) => setLinkParticipantName(e.target.value.replace(/\d/g, ''))}
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Email *</label>
                      <input
                        type="email"
                        value={linkParticipantEmail}
                        onChange={(e) => setLinkParticipantEmail(e.target.value)}
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleCreatePublicLink} disabled={loading}>
                        Criar Link
                      </Button>
                      <Button variant="outline" onClick={() => setShowLinkForm(false)}>
                        Cancelar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {publicLinks.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <LinkIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhum link público criado</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {publicLinks.map(link => (
                    <div key={link.id} className="p-4 border rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold">{link.participant_name}</h3>
                          <p className="text-sm text-gray-600">{link.participant_email}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Expira em: {new Date(link.expires_at).toLocaleDateString('pt-BR')}
                          </p>
                          {link.completed && (
                            <span className="inline-flex items-center gap-1 mt-2 text-sm text-green-600">
                              <CheckCircle className="h-4 w-4" />
                              Avaliação Concluída
                            </span>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyLinkToClipboard(link.token)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          {user?.is_admin && (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleExcluirLink(link.id, link.participant_name)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>}
      </Tabs>
    </div>
  );
};

export default ExcelAssessmentPage;
